import {useMemo} from 'react'

import {
  showImgsProductListSelector,
  usesInventorySelector,
} from '../../../data/company.js'
import LinkButton from '../../../common/components/Link/LinkButton.js'
import ButtonLink from '../../../common/components/Button/ButtonLink.js'
import {
  nameSelector,
  createTagsSelector,
  productComponentsSelector,
  isKitSelector,
  isBOMSelector,
  isArchivedSelector,
  imageURLSelector,
  isDigitalSelector,
} from '../../../data/products.js'
import {
  productDetailURLSelector,
  getProductDetailURL,
} from '../../ProductDetailPage/productDetailSelectors.js'
import {showProductTextFieldModal} from '../Modals/ProductTextFieldModal.js'

import AdditionalInfo from './AdditionalInfo/index.js'
import ProductTags from '../ProductTags.js'
import PopoverImage from '../../../common/components/PopoverImage.js'
import Icon from '../../../common/icons/Icon.js'
import {useSelector} from '../../../store.js'

export default function NameCell({sku, isExpanded}) {
  const tagsSelector = useMemo(() => createTagsSelector(sku), [sku])
  const name = useSelector((state) => nameSelector(state, {sku}))
  const tags = useSelector(tagsSelector)
  const productDetailURL = useSelector((state) =>
    productDetailURLSelector(state, {sku}),
  )
  const productComponents = useSelector((state) =>
    productComponentsSelector(state, {sku}),
  )
  const isKit = useSelector((state) => isKitSelector(state, {sku}))
  const isBOM = useSelector((state) => isBOMSelector(state, {sku}))
  const isArchived = useSelector((state) => isArchivedSelector(state, {sku}))
  const isDigital = useSelector((state) => isDigitalSelector(state, {sku}))
  const imageURL = useSelector((state) => imageURLSelector(state, {sku}))
  const usesInventory = useSelector(usesInventorySelector)
  const showImgsProductList = useSelector(showImgsProductListSelector)

  const tagComponent =
    tags.length > 0 ? (
      <div
        className={
          isExpanded
            ? 'wrap--po-addl-info list list--order-data'
            : 'wrap--summary-tags-table wrap--summary-tags-inline'
        }
      >
        <ProductTags sku={sku} tags={tags} />
      </div>
    ) : null

  if (!usesInventory) {
    return (
      <td className="table__td table__td--product-list-name img-enabled">
        <div className="flex">
          {showImgsProductList && <PopoverImage src={imageURL} alt={name} />}
          <div>
            <div className="wrap--product-name">
              <LinkButton
                className="darkest lh-md margin-right-5"
                href={productDetailURL}
              >
                <strong>{name}</strong>
              </LinkButton>
              {isArchived && (
                <div className="label__callout label__callout--grey inline-block margin-right-5">
                  <strong>ARCHIVED</strong>
                </div>
              )}
              {isDigital && (
                <div className="label__callout label__callout--grey inline-block margin-right-5">
                  <strong title="Digital">D</strong>
                </div>
              )}
              <ButtonLink
                className="btn--edit-product-name no-underline"
                onClick={() =>
                  showProductTextFieldModal({
                    sku,
                    value: name,
                    display: 'Name',
                    apiProp: 'name',
                    isRequired: true,
                    needsProductListRefresh: true,
                  })
                }
              >
                <Icon name="pencil" className="fs-00 lh-sm v-align-middle" />
              </ButtonLink>
            </div>
            {tagComponent}
          </div>
        </div>
      </td>
    )
  }

  return (
    <td
      className={`table__td table__td--product-list-name ${
        showImgsProductList ? 'img-enabled' : ''
      }`}
    >
      <div className="flex">
        {showImgsProductList && <PopoverImage src={imageURL} alt={name} />}
        <div>
          <div className="wrap--product-name">
            <LinkButton
              className="darkest lh-md margin-right-5"
              href={productDetailURL}
            >
              <strong>{name}</strong>
            </LinkButton>
            {isKit && (
              <LinkButton
                className="label__callout label__callout--grey no-underline margin-right-5"
                href={`${productDetailURL}/kitting`}
              >
                <strong>KIT</strong>
              </LinkButton>
            )}
            {isBOM && (
              <LinkButton
                className="label__callout label__callout--grey no-underline margin-right-5"
                href={`${productDetailURL}/bom`}
              >
                <strong>BOM</strong>
              </LinkButton>
            )}
            {isArchived && (
              <div className="label__callout label__callout--grey inline-block margin-right-5">
                <strong>ARCHIVED</strong>
              </div>
            )}
            {isDigital && (
              <div className="label__callout label__callout--grey inline-block margin-right-5">
                <strong title="Digital">D</strong>
              </div>
            )}
            <ButtonLink
              className="btn--edit-product-name no-underline"
              onClick={() =>
                showProductTextFieldModal({
                  sku,
                  value: name,
                  display: 'Name',
                  apiProp: 'name',
                  isRequired: true,
                  needsProductListRefresh: true,
                })
              }
            >
              <Icon name="pencil" className="fs-00 lh-sm v-align-middle" />
            </ButtonLink>
          </div>
          {isExpanded && productComponents.length > 0 && (
            <AdditionalInfo>
              {productComponents.map((component) => {
                const item = (
                  <AdditionalInfo.Item key={component.id}>
                    <AdditionalInfo.ItemDisplay>
                      <LinkButton
                        className="darkest"
                        href={getProductDetailURL(component.sku)}
                      >
                        <span className="unbold">{component.name}</span>
                      </LinkButton>
                      <div className="text--lt-grey unbold fs-n2">
                        ({component.total_on_hand} on hand)
                      </div>
                    </AdditionalInfo.ItemDisplay>
                    <AdditionalInfo.ItemValue>
                      <span>{component.quantity}</span>
                    </AdditionalInfo.ItemValue>
                  </AdditionalInfo.Item>
                )

                return showImgsProductList ? (
                  <div
                    className="flex wrap__inner--product-addl-info-list"
                    key={component.id}
                  >
                    <PopoverImage
                      src={component.image_url}
                      alt={component.name}
                    />
                    {item}
                  </div>
                ) : (
                  item
                )
              })}
            </AdditionalInfo>
          )}
          {tagComponent}
        </div>
      </div>
    </td>
  )
}
